/**
 * Water Report – postcode + email → API returns scores for every Australian postcode.
 * When apiUrl is set: POST { postcode, email } to apiUrl/water-report; show location, scores, optional official link.
 * When apiUrl is blank: show message to configure API in theme settings (no static fallback).
 */
(function () {
  'use strict';

  function normalisePostcode(value) {
    var s = String(value).replace(/\s/g, '');
    return s.length <= 4 ? s : s.slice(0, 4);
  }

  function isValidPostcode(value) {
    var n = normalisePostcode(value);
    return /^\d{4}$/.test(n);
  }

  function isValidEmail(value) {
    var s = String(value).trim();
    return s.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  function show(el, showIt) {
    if (!el) return;
    el.classList.toggle('is-visible', !!showIt);
  }

  function setHtml(el, html) {
    if (!el) return;
    el.innerHTML = html;
  }

  function escapeHtml(s) {
    if (s == null || s === '') return '';
    var div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  function escapeAttr(s) {
    if (s == null || s === '') return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function formatScore(num) {
    if (num == null || num === '') return '—';
    var n = Number(num);
    if (isNaN(n)) return '—';
    return Math.round(n);
  }

  function renderApiReport(data, strings) {
    var viewOfficial = (strings && strings.viewOfficialReport) || 'View official report';
    var overallLabel = (strings && strings.overallScore) || 'Overall score';
    var chlorineLabel = (strings && strings.chlorineScore) || 'Chlorine score';
    var metalsLabel = (strings && strings.metalsScore) || 'Heavy metals score';
    var note = (strings && strings.representativeNote) || '';

    var parts = [];
    parts.push('<div class="water-report-card">');

    var location = [data.suburb, data.state].filter(Boolean).join(', ');
    if (location) parts.push('<p class="subtext-lg font-medium">' + escapeHtml(location) + '</p>');
    if (data.system_name) parts.push('<p class="subtext-md opacity-80">' + escapeHtml(data.system_name) + '</p>');

    parts.push('<div class="water-report-scores mt-4 flex flex-wrap gap-6">');
    if (data.overall_score != null) {
      parts.push('<div><span class="subtext-sm opacity-80">' + escapeHtml(overallLabel) + '</span><p class="font-medium">' + formatScore(data.overall_score) + '/100</p></div>');
    }
    if (data.chlorine_score != null) {
      parts.push('<div><span class="subtext-sm opacity-80">' + escapeHtml(chlorineLabel) + '</span><p class="font-medium">' + formatScore(data.chlorine_score) + '/100</p></div>');
    }
    if (data.metals_score != null) {
      parts.push('<div><span class="subtext-sm opacity-80">' + escapeHtml(metalsLabel) + '</span><p class="font-medium">' + formatScore(data.metals_score) + '/100</p></div>');
    }
    if (data.hardness_score != null) {
      parts.push('<div><span class="subtext-sm opacity-80">Hardness</span><p class="font-medium">' + formatScore(data.hardness_score) + '/100</p></div>');
    }
    parts.push('</div>');

    if (note) parts.push('<p class="mt-4 subtext-sm opacity-80">' + escapeHtml(note) + '</p>');
    if (data.official_url) {
      parts.push('<p class="mt-4 subtext-sm"><a href="' + escapeAttr(data.official_url) + '" class="link" target="_blank" rel="noopener">' + escapeHtml(viewOfficial) + '</a></p>');
    }
    parts.push('</div>');
    return parts.join('');
  }

  function renderNotConfigured(strings) {
    var msg = (strings && strings.apiNotConfigured) || 'Reports with scores are available for every Australian postcode. Add your Water Report API URL in Theme settings (Water report section) to enable this feature.';
    return '<div class="water-report-card"><p class="subtext-md">' + escapeHtml(msg) + '</p></div>';
  }

  function init(formSelector, options) {
    var form = document.querySelector(formSelector);
    if (!form) return;

    var postcodeInput = document.querySelector(options.postcodeInput || '#WaterReportPostcode');
    var emailInput = document.querySelector(options.emailInput);
    var submitBtn = document.querySelector(options.submitBtn);
    var loadingEl = document.querySelector(options.loadingEl);
    var errorEl = document.querySelector(options.errorEl);
    var resultsEl = document.querySelector(options.resultsEl);
    var apiUrl = (options.apiUrl || '').trim().replace(/\/+$/, '');
    var strings = options.strings || {};

    if (!resultsEl) return;
    var useApi = apiUrl.length > 0;

    if (!useApi) {
      setHtml(resultsEl, renderNotConfigured(strings));
      show(resultsEl, true);
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var rawPostcode = postcodeInput && postcodeInput.value ? postcodeInput.value.trim() : '';
      var rawEmail = emailInput && emailInput.value ? emailInput.value.trim() : '';

      if (!isValidPostcode(rawPostcode)) {
        show(loadingEl, false);
        show(resultsEl, false);
        setHtml(errorEl, '<div class="alert alert--error">' + (strings.invalidPostcode || 'Please enter a valid 4-digit Australian postcode.') + '</div>');
        show(errorEl, true);
        return;
      }
      if (useApi && !isValidEmail(rawEmail)) {
        show(loadingEl, false);
        show(resultsEl, false);
        setHtml(errorEl, '<div class="alert alert--error">' + (strings.invalidEmail || 'Please enter a valid email address.') + '</div>');
        show(errorEl, true);
        return;
      }

      if (!useApi) {
        setHtml(resultsEl, renderNotConfigured(strings));
        show(resultsEl, true);
        return;
      }

      show(errorEl, false);
      setHtml(errorEl, '');
      show(resultsEl, false);
      show(loadingEl, true);
      if (submitBtn) submitBtn.disabled = true;

      var url = apiUrl + '/water-report';
      var controller = new AbortController();
      var timeoutId = setTimeout(function () { controller.abort(); }, 15000);
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcode: normalisePostcode(rawPostcode), email: rawEmail }),
        signal: controller.signal
      })
        .then(function (res) {
          clearTimeout(timeoutId);
          if (!res.ok) return res.json().then(function (body) { throw new Error(body.detail || body.message || 'Request failed'); }).catch(function () { throw new Error('Request failed'); });
          return res.json();
        })
        .then(function (data) {
            show(loadingEl, false);
            if (submitBtn) submitBtn.disabled = false;
            setHtml(resultsEl, renderApiReport(data, strings));
            show(resultsEl, true);
            resultsEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        })
        .catch(function (err) {
          clearTimeout(timeoutId);
          show(loadingEl, false);
          if (submitBtn) submitBtn.disabled = false;
          var msg = err.name === 'AbortError' ? 'The request took too long. The report service may be busy—please try again.' : (err.message || strings.notFound || 'Unable to load report. Please try again.');
          setHtml(errorEl, '<div class="alert alert--error">' + escapeHtml(msg) + '</div>');
          show(errorEl, true);
        });
    });
  }

  window.WaterReport = { init: init };
})();
